import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { injectIntl, FormattedMessage } from 'react-intl';

import { LoginCell, MoneyCell, BaseTable, BasicModal, ActionsButton, Tooltip } from 'common/components';

import { withTheme } from 'common/styling/theme';
import noInfoImage from 'common/images/noInfo.svg';
import { EditIcon } from 'common/icons';

import UserNoInfoPanel from '../../../_components/UserNoInfoPanel';
import UserPartnerAccountEditForm from '../UserPartnerAccountEditForm';

import { staticStyles, getDynamicStyles } from './style';

const UserPartnerAccountsTable = ({
  data,
  rewardFrequencies,
  accountCreatePerm,
  intl,
  partnershipConditionsList,
  userId,
  getUserPartnerAccounts,
  getPartnershipConditionsList,
  simplePartnerSystem,
  theme,
}) => {
  const [selectedAccount, setSelectedAccount] = useState(undefined);
  const [partnerCode, setPartnerCode] = useState(undefined);

  const openEditUserPartnerAccountModal = (accountId, partnerCode) => {
    setSelectedAccount(accountId);
    setPartnerCode(partnerCode);
  };

  const closeEditUserPartnerAccountModal = () => {
    setSelectedAccount(undefined);
    setPartnerCode(undefined);
  };

  const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;

  const REWARD_FREQUENCIES = {
    dailyInstant: intl.formatMessage({ id: 'justDailyInstant' }),
    monthlyInstant: intl.formatMessage({ id: 'justMonthlyInstant' }),
    monthlyDelayed: intl.formatMessage({ id: 'justMonthlyDelayed' }),
  };

  // 程序名称映射函数
  const getProgramDisplayName = program => {
    if (program === 'traded_volume') {
      return 'Traded Volume';
    }
    if (program === 'pnl') {
      return 'Profit & Loss';
    }
    if (program === 'net_deposit') {
      return 'Net Deposit Sharing';
    }
    if (program === 'percentage') {
      return 'Percentage';
    }
    return program || '';
  };

  const getPartnershipConditionIds = account => {
    if (Array.isArray(account.partnershipConditionsMultiple) && account.partnershipConditionsMultiple.length > 0) {
      return account.partnershipConditionsMultiple;
    }

    if (account.partnershipConditions?.title) {
      const condition = partnershipConditionsList.find(item => item.title === account.partnershipConditions.title);
      return condition ? [condition.id] : [];
    }

    return [];
  };

  const initialPartnershipConditions = useMemo(() => {
    const selectedItem = data.find(item => item.id === selectedAccount);
    return selectedItem ? getPartnershipConditionIds(selectedItem) : [];
  }, [data, partnershipConditionsList, selectedAccount]);

  const initialFrequency = useMemo(
    () => data.find(item => item.id === selectedAccount)?.frequencyOfPayments,
    [data, selectedAccount]
  );

  /* eslint-disable */
  const editColumn =
    accountCreatePerm && !simplePartnerSystem
      ? [
        {
          Cell: ({ original: { id, partnerCode } }) => (
            <ActionsButton
              edit
              onClickFunc={e => {
                e.stopPropagation();
                openEditUserPartnerAccountModal(id, partnerCode);
              }}>
              <EditIcon />
            </ActionsButton>
          ),
          isPreviewCell: true,
        },
      ]
      : [];

  let advancePartnerSystemColumns;
  if (!simplePartnerSystem) {
    advancePartnerSystemColumns = [
      {
        key: 'rewardType',
        Header: () => <FormattedMessage id="ibRewardType" />,
        Cell: ({ original: account }) => {
          const conditionIds = getPartnershipConditionIds(account);
          if (conditionIds.length > 0) {
            return (
              <div className="RewardType__container">
                {partnershipConditionsList
                  .filter(condition => conditionIds.includes(condition.id))
                  .map((condition, index) => {
                    const tooltipId = `rewardType-tooltip-${account.id}-${index}`;
                    if (condition.isDifferentTemplates) {
                      const aBookContent = getProgramDisplayName(condition.aBookProgram);
                      const bBookContent = getProgramDisplayName(condition.bBookProgram);
                      const fullContent = `A-Book: ${aBookContent}、B-Book: ${bBookContent}`;
                      return (
                        <><Tooltip key={index} id={tooltipId} textId={fullContent} noTranslate>
                          <div className="RewardType__condition-item">
                            <div>
                              <p>A-Book: {aBookContent}、B-Book: {bBookContent}</p>
                            </div>
                          </div>
                        </Tooltip><br /></>
                      );
                    } else {
                      const content = getProgramDisplayName(condition.tableProgram);
                      return (
                        <>
                          <Tooltip key={index} id={tooltipId} textId={content} noTranslate>
                            <div className="RewardType__condition-item">
                              <p className="RewardType__cell-text">{content}</p>
                            </div>
                          </Tooltip>
                          <br />
                        </>
                      );
                    }
                  })}
              </div>
            );
          }
        },
      },
      {
        key: 'masterRewardType',
        Header: () => <FormattedMessage id="masterIbRewardType" />,
        Cell: ({ original: account }) => {
          const conditionIds = getPartnershipConditionIds(account);
          if (conditionIds.length > 0) {
            return (
              <div className="RewardType__container">
                {partnershipConditionsList
                  .filter(condition => conditionIds.includes(condition.id))
                  .map((condition, index) => {
                    const tooltipId = `masterRewardType-tooltip-${account.id}-${index}`;
                    if (condition.subIbEnabled) {
                      const masterABookContent = getProgramDisplayName(condition.masterABookTableProgram);
                      const masterBBookContent = getProgramDisplayName(condition.masterBBookTableProgram);
                      const fullContent = `A-Book: ${masterABookContent}、B-Book: ${masterBBookContent}`;
                      return (
                        <><Tooltip key={index} id={tooltipId} textId={fullContent} noTranslate>
                          <div className="RewardType__condition-item">
                            <div>
                              <p>A-Book: {masterABookContent}、B-Book: {masterBBookContent}</p>
                            </div>
                          </div>
                        </Tooltip><br /></>
                      );
                    } else {
                      const content = getProgramDisplayName(condition.masterRewardTableProgram);
                      return (
                        <>
                          <Tooltip key={index} id={tooltipId} textId={content} noTranslate>
                            <div className="RewardType__condition-item">
                              <p className="RewardType__cell-text">{content}</p>
                            </div>
                          </Tooltip>
                          <br />
                        </>
                      );
                    }
                  })}
              </div>
            );
          }
        },
      },
      {
        key: 'rewardTemplate',
        Cell: ({ original: account }) => {
          const conditionIds = getPartnershipConditionIds(account);
          if (conditionIds.length > 0) {
            return (
              <div>
                {partnershipConditionsList
                  .filter(condition => conditionIds.includes(condition.id))
                  .map((condition, index) => {
                    const tooltipId = `rewardTemplate-tooltip-${account.id}-${index}`;
                    return (
                      <>
                        <Tooltip key={index} id={tooltipId} textId={condition.title} noTranslate>
                          <p className="RewardType__condition-item RewardType__cell-text">{condition.title}</p>
                        </Tooltip>
                        <br />
                      </>
                    );
                  })}
              </div>
            );
          }
        },
      },
      {
        key: 'justBalance',
        Cell: ({ original: { balance, loading, loadingFailed } }) => (
          <MoneyCell inTable value={balance} loading={loading} loadingFailed={loadingFailed} />
        ),
        isPreviewCell: true,
      },
      {
        key: 'justFrequencyOfReward',
        Cell: ({ original: { frequencyOfPayments } }) => (
          <p className="RewardType__cell">{REWARD_FREQUENCIES[frequencyOfPayments] ?? (frequencyOfPayments || '—')}</p>
        ),
      },
    ];
  } else {
    advancePartnerSystemColumns = [];
  }

  const columns = [
    {
      key: 'accountId',
      Cell: ({ original: { partnerCode } }) => (
        <LoginCell login={partnerCode} title={intl.formatMessage({ id: 'partnerAccount' })} inTable />
      ),
      isPreviewCell: true,
    },
    ...advancePartnerSystemColumns,
    ...editColumn,
  ];
  /* eslint-enable */
  return (
    <div
      className={classNames('UserPartnerAccountsTable', {
        UserPartnerAccountsTable__noAccount: !data.length,
        UserPartnerAccountsTable__small: simplePartnerSystem,
      })}>
      {data.length ? (
        <div className="UserPartnerAccountsTable__body">
          <BaseTable columns={columns} data={data} isResponsive />
        </div>
      ) : (
        <UserNoInfoPanel image={noInfoImage} textId="boNoPartnerAccounts" />
      )}

      <BasicModal
        captionId="editPartnerAccountCaption"
        isOpen={!!selectedAccount}
        onRequestClose={closeEditUserPartnerAccountModal}>
        {!!selectedAccount && (
          <UserPartnerAccountEditForm
            accountId={selectedAccount}
            partnershipConditionsList={partnershipConditionsList}
            partnerCode={+partnerCode}
            rewardFrequencies={rewardFrequencies}
            getPartnershipConditionsList={getPartnershipConditionsList}
            userId={+userId}
            onSubmitSuccess={() => {
              closeEditUserPartnerAccountModal();
              getUserPartnerAccounts(userId);
            }}
            initialValues={{
              partnershipConditions: initialPartnershipConditions,
              frequencyOfPayments: initialFrequency,
            }}
          />
        )}
      </BasicModal>

      <style jsx>{staticStyles}</style>
      <style jsx>{dynamicStyles}</style>
    </div>
  );
};

UserPartnerAccountsTable.propTypes = {
  data: PropTypes.array.isRequired,
  rewardFrequencies: PropTypes.array.isRequired,
  intl: PropTypes.object.isRequired,
  accountCreatePerm: PropTypes.bool.isRequired,
  partnershipConditionsList: PropTypes.array.isRequired,
  userId: PropTypes.string.isRequired,
  getUserPartnerAccounts: PropTypes.func.isRequired,
  getPartnershipConditionsList: PropTypes.func.isRequired,
  simplePartnerSystem: PropTypes.bool.isRequired,
  theme: PropTypes.object,
};

UserPartnerAccountsTable.defaultProps = {
  theme: {},
};

export default withTheme()(injectIntl(UserPartnerAccountsTable));
export { UserPartnerAccountsTable };
